import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export interface CartItem {
  productId: number
  name: string
  imageUrl: string | null
  quantity: number
}

interface CartContextValue {
  items: CartItem[]
  itemCount: number
  isOpen: boolean
  addItem: (item: Omit<CartItem, 'quantity'>, quantity: number, maxStock?: number) => void
  updateQuantity: (productId: number, quantity: number) => void
  removeItem: (productId: number) => void
  clearCart: () => void
  openCart: () => void
  closeCart: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

const STORAGE_KEY = 'mmc_cart_items'

function readStoredItems(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function sameLine(a: CartItem, productId: number) {
  return a.productId === productId
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => readStoredItems())
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }, [items])

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      isOpen,
      addItem: (item, quantity, maxStock) => {
        setItems((current) => {
          const existing = current.find((i) => sameLine(i, item.productId))
          // El tope de stock solo aplica al incrementar una línea ya existente en el
          // carrito, nunca al agregar la primera unidad (comportamiento intencional).
          if (existing) {
            const nextQuantity = maxStock != null ? Math.min(existing.quantity + quantity, maxStock) : existing.quantity + quantity
            return current.map((i) => (sameLine(i, item.productId) ? { ...i, quantity: nextQuantity } : i))
          }
          return [...current, { ...item, quantity }]
        })
      },
      updateQuantity: (productId, quantity) => {
        setItems((current) => {
          if (quantity <= 0) return current.filter((i) => !sameLine(i, productId))
          return current.map((i) => (sameLine(i, productId) ? { ...i, quantity } : i))
        })
      },
      removeItem: (productId) => {
        setItems((current) => current.filter((i) => !sameLine(i, productId)))
      },
      clearCart: () => setItems([]),
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
    }),
    [items, isOpen],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart debe usarse dentro de <CartProvider>')
  return ctx
}
